import { Avatar, Box, Chip, CircularProgress, Menu, MenuItem, TextField, Typography } from "@mui/material";
import { colors } from "../../theme/palette";
import { useState, useEffect, useRef } from "react";
import { UserInfo } from "../../types";
import { getUserData } from "../../utils/userDataFormatter";
import { useTranslation } from "react-i18next";
import { getUsersToChatWith } from "../../api/services/entities";
import { useProfileData } from "../../hooks/useProfileData";

const TextFieldWithChip = ({
  setSearchedReceiverName,
  setSearchedReceiverId,
}: {
  setSearchedReceiverName: React.Dispatch<React.SetStateAction<string | null>>;
  setSearchedReceiverId: React.Dispatch<React.SetStateAction<number | null>>;
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const { data: userData, isLoading } = useProfileData();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [options, setOptions] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);

  const ignoreFetch = useRef(false);

  const fetchUsers = async (searchText: string) => {
    setLoading(true);
    try {
      if (!userData?.entityId) return;
      const users = await getUsersToChatWith(userData.entityId, searchText);
      setLoading(false);
      setOptions(users.items);
      setAnchorEl(document.getElementById("search-text-field"));
    } catch (error) {
      setLoading(false);
      setOptions([]);
    }
  };

  useEffect(() => {
    if (inputValue.length < 2) return;

    const handler = setTimeout(() => {
      if (!ignoreFetch.current) {
        setDebouncedValue(inputValue);
      }
      ignoreFetch.current = false;
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  useEffect(() => {
    if (debouncedValue) {
      fetchUsers(debouncedValue);
    }
  }, [debouncedValue]);

  const handleReceiverSelection = (user: UserInfo) => {
    const { firstName, lastName, id } = user;
    const fullName = `${firstName} ${lastName}`;
    setInputValue("");
    setSearchedReceiverName(fullName);
    setSearchedReceiverId(id);
    setSelectedUser(user);
    setAnchorEl(null);

    ignoreFetch.current = true;
  };

  const handleDeleteChip = () => {
    setSelectedUser(null);
    setSearchedReceiverName(null);
    setSearchedReceiverId(null);
    setInputValue("");
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !inputValue && selectedUser) {
      handleDeleteChip();
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ margin: "0 28px" }}>
      <TextField
        id="search-text-field"
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          ignoreFetch.current = false; // Allow fetching again when input changes
        }}
        onKeyDown={handleInputKeyDown} // Handle backspace for chip deletion
        placeholder={selectedUser ? "" : t("components.newChatPopUp.searchWhomeYouWant")}
        InputProps={{
          startAdornment: selectedUser ? (
            <Chip
              avatar={<Avatar>{selectedUser.firstName.charAt(0)}</Avatar>}
              label={`${selectedUser.firstName} ${selectedUser.lastName}`}
              onDelete={handleDeleteChip} // Add delete icon
              sx={{
                marginRight: "8px", // Space between chip and text input
              }}
            />
          ) : null,
          style: {
            fontSize: "14px",
            fontWeight: selectedUser ? 600 : 400,
            color: selectedUser ? colors.Green100 : colors.Gray400,
          },
          endAdornment: loading ? <CircularProgress color="inherit" size={20} /> : null,
        }}
        fullWidth
        sx={{
          marginBottom: "37px",
          "& .MuiInputBase-root": {
            paddingRight: currentLanguage === "ar" ? "9px !important" : "unset",
          },
        }}
        disabled={!!selectedUser} // Disable the text field if a user is selected
      />

      <Menu
        id="search-options-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        MenuListProps={{
          sx: {
            maxHeight: 250, // To control menu height if there are many options
          },
        }}
      >
        {options?.length === 0 && !loading ? (
          <MenuItem disabled>
            <Typography variant="body16Regular">{t("components.newChatPopUp.noUsersFound")}</Typography>
          </MenuItem>
        ) : (
          options?.map((option) => {
            const { media, fullName, role, firstTwoChr, id } = getUserData(option);
            return (
              <MenuItem
                key={id}
                onClick={() => handleReceiverSelection(option)}
                sx={{ display: "flex", columnGap: "12px" }}
              >
                <Avatar style={{ width: "48px", height: "48px" }} src={media}>
                  {firstTwoChr}
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body16Regular">{fullName}</Typography>
                  <Typography variant="body12Regular" sx={{ color: colors.Dark600 }}>
                    {role}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
};

export default TextFieldWithChip;
